import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Checklist as ChecklistIcon,
} from '@mui/icons-material';
import { CircularProgress as CircularProgressIcon } from '@mui/material';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import { Button, IconButton } from '../button';
import { Badge } from '../badge';
import { Checkbox } from '../checkbox';
import { Chip } from '../chip';
import { Input } from '../input';
import { Typography } from '../typography';
import {
  ChatWrapper,
  ChatPanel,
  ChatHeader,
  ChatHeaderTitle,
  ChatHeaderMainTitle,
  ChatBody,
  ChatFooter,
  LoadingOverlay,
  ChatItemGap,
  MessageContent,
  StyledVirtuosoWrapper,
  ChatItem,
  ChatItemContent,
  ChatItemCheckboxWrapper,
  ChatItemBottomRow,
  MessagesContainer,
  MessageRow,
  MessageBubble,
  MessageTime,
  EmptyState,
} from './ChatComponents';
import { useChatQuery, useChatsQuery, useSendMessageMutation, useIsSmallScreen } from '../../hooks';
import { ChatGetAllQuery, ChatGetOneQuery } from '../../types';
import { momentTextFormat, showToRoles } from '../../utils';
import { useStoreUser } from '../../context';
import { removeCache } from '../../services';
import { i18n } from '../../i18n';

export const Chat: React.FC = () => {
  const { user } = useStoreUser();
  const [selectedChatId, setSelectedChatId] = useState<string>('');
  const { pathname } = useLocation();
  const isPageLoaded = useRef(false);
  const isSmallScreen = useIsSmallScreen();

  const {
    data: { data } = { data: [] },
    isFetching: isLoadingChats,
    refetch: refetchChats,
  } = useChatsQuery();
  const {
    data: { messages = [] } = { messages: [] },
    isFetching: isLoadingMessages,
    refetch: refetchMessages,
  } = useChatQuery(selectedChatId);
  const sendMessageMutation = useSendMessageMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([]);

  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedChat = useMemo(
    () => data.find((chat) => chat.id === selectedChatId) || null,
    [data, selectedChatId],
  );

  const isLastMessageMine = useMemo(() => {
    return messages.length > 0 && messages[messages.length - 1]?.senderId === user.id;
  }, [messages]);

  const isSupportChat = useMemo(() => {
    return selectedChat?.type === 'SUPER_ADMIN_CLIENT' && !showToRoles(['SUPER_ADMIN'], user);
  }, [selectedChat]);

  const chatTitle = useMemo(() => {
    if (isSupportChat) {
      return i18n.t('pages.chat.supportChat');
    }

    if (selectedChat?.type === 'SUPER_ADMIN_CLIENT') {
      return i18n.t('common.client');
    }

    return i18n.t('pages.chat.title');
  }, [selectedChat]);

  useEffect(() => {
    if (!isPageLoaded.current) {
      isPageLoaded.current = true;
      return;
    }

    removeCache(['chats', 'chat']);
  }, [pathname]);

  useEffect(() => {
    if (!isOpen || isLoadingChats) return;

    if (data.length === 1 && data[0].isSupport) {
      handleSelectChat(data[0].id);
    }
  }, [isOpen, isLoadingChats]);

  useEffect(() => {
    if (messages.length > 0 && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: messages.length - 1,
        behavior: 'smooth',
      });

      if (showToRoles(['SUPER_ADMIN'], user)) {
        inputRef.current?.focus();
      }
    }
  }, [messages]);

  const handleToggleOpen = () => {
    if (isOpen) {
      handleClosePanel();
    } else {
      setIsOpen(true);
    }
  };

  const handleBackButton = () => {
    removeCache(['chats']);
    setSelectedChatId('');

    refetchChats();
  };

  const handleClosePanel = () => {
    setIsOpen(false);
    handleBackButton();
  };

  const handleSelectChat = (chatId: string) => {
    if (selectedChatIds.length) return handleSelectChatId(chatId);
    if (chatId === selectedChatId) return;

    setSelectedChatId(chatId);
  };

  const handleSelectChatId = (chatId: string) => {
    setSelectedChatIds((prev) =>
      prev.includes(chatId) ? prev.filter((id) => id !== chatId) : [...prev, chatId],
    );
  };

  const handleSendMessage = async (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }

    if (
      !messageText.trim() ||
      (selectedChatIds.length === 0 && !selectedChatId) ||
      isLoadingMessages
    )
      return;

    const trimmedText = messageText.trim();

    const chunks: string[] = [];
    for (let i = 0; i < trimmedText.length; i += 2000) {
      chunks.push(trimmedText.slice(i, i + 2000));
    }

    setMessageText('');

    if (selectedChatIds.length) {
      for (const chunk of chunks) {
        await Promise.all(
          selectedChatIds.map((chatId) =>
            sendMessageMutation.mutateAsync({ id: chatId, content: chunk }),
          ),
        );
      }
      removeCache(['chats']);
      refetchChats();
    } else {
      await Promise.all(
        chunks.map((chunk) =>
          sendMessageMutation.mutateAsync({ id: selectedChatId, content: chunk }),
        ),
      );
      refetchMessages();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const showChatsList = !selectedChatId;

  return (
    <ChatWrapper $isSmallScreen={!isOpen}>
      {isOpen && (
        <ChatPanel $isFullscreen={isSmallScreen}>
          <ChatHeader>
            {selectedChat && !isSupportChat ? (
              <IconButton size="medium" onClick={handleBackButton}>
                <ArrowBackIcon fontSize="medium" />
              </IconButton>
            ) : (
              <span />
            )}

            <ChatHeaderTitle>
              <ChatHeaderMainTitle>
                <ChatBubbleOutlineIcon
                  fontSize={isSupportChat && !isLoadingMessages ? 'medium' : 'small'}
                />
                <Typography
                  variant={isSupportChat && !isLoadingMessages ? 'textL' : 'textM'}
                  weight="medium"
                >
                  {chatTitle}
                </Typography>
              </ChatHeaderMainTitle>
              <Typography variant="textXS" color="--gray-60">
                {showChatsList && i18n.t('pages.chat.chooseChatFromList')}
                {selectedChat &&
                  !isLoadingMessages &&
                  !isSupportChat &&
                  i18n.t('pages.chat.name', { name: selectedChat.client?.legalName })}
                {isLoadingMessages && i18n.t('label.loading')}
              </Typography>
            </ChatHeaderTitle>

            <IconButton size="medium" onClick={handleClosePanel}>
              <CloseIcon fontSize="medium" />
            </IconButton>
          </ChatHeader>

          <ChatBody>
            {showChatsList && (
              <>
                <StyledVirtuosoWrapper>
                  <Virtuoso<ChatGetAllQuery['data'][number]>
                    data={data}
                    itemContent={(index, chat) => (
                      <ChatItemGap isLast={index === data.length - 1}>
                        <ChatItem
                          key={chat.id}
                          isActive={chat.id === selectedChatId}
                          isOdd={index % 2 === 1}
                        >
                          <ChatItemCheckboxWrapper onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedChatIds.includes(chat.id)}
                              onChange={() => handleSelectChatId(chat.id)}
                            />
                          </ChatItemCheckboxWrapper>
                          <ChatItemContent onClick={() => handleSelectChat(chat.id)}>
                            <Typography variant="textM" weight="medium">
                              {chat.client?.legalName}
                            </Typography>
                            <ChatItemBottomRow>
                              <Typography variant="textXS" color="--gray-60">
                                {momentTextFormat(chat.updatedAt, user)}
                              </Typography>
                              {chat._count?.messages ? (
                                <Chip color="blue" size="small" label={chat._count?.messages} />
                              ) : null}
                            </ChatItemBottomRow>
                          </ChatItemContent>
                        </ChatItem>
                      </ChatItemGap>
                    )}
                  />
                </StyledVirtuosoWrapper>

                {!isLoadingChats && data.length === 0 && (
                  <EmptyState>
                    <Typography variant="textS" color="--gray-70">
                      {i18n.t('label.notFound')}
                    </Typography>
                  </EmptyState>
                )}
              </>
            )}

            {!showChatsList && (
              <MessagesContainer>
                {messages.length === 0 && !isLoadingMessages ? (
                  <EmptyState>
                    <Typography variant="textS" color="--gray-70">
                      {i18n.t('label.notFound')}
                    </Typography>
                  </EmptyState>
                ) : (
                  <StyledVirtuosoWrapper>
                    <Virtuoso<ChatGetOneQuery['data']['messages'][number]>
                      ref={virtuosoRef}
                      data={messages}
                      initialTopMostItemIndex={messages.length - 1}
                      followOutput="smooth"
                      itemContent={(_, message) => {
                        const isMine = message.isMine;
                        const isRead = isLastMessageMine || isMine || message.isRead;
                        return (
                          <MessageRow alignRight={isMine}>
                            <MessageContent isMine={isMine}>
                              <Badge
                                badgeContent=""
                                data-color="blue"
                                data-size="small"
                                invisible={isRead}
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                              >
                                <MessageBubble isMine={isMine}>{message.content}</MessageBubble>
                              </Badge>
                              <MessageTime isMine={isMine}>
                                {momentTextFormat(message.createdAt, user)}
                              </MessageTime>
                            </MessageContent>
                          </MessageRow>
                        );
                      }}
                    />
                  </StyledVirtuosoWrapper>
                )}
              </MessagesContainer>
            )}

            {isLoadingChats && (
              <LoadingOverlay>
                <CircularProgressIcon sx={{ color: 'var(--white)' }} />
              </LoadingOverlay>
            )}

            {isLoadingMessages && !showChatsList && (
              <LoadingOverlay>
                <CircularProgressIcon sx={{ color: 'var(--white)' }} />
              </LoadingOverlay>
            )}
          </ChatBody>

          {showToRoles(['SUPER_ADMIN'], user) && (
            <ChatFooter $hasMessageText={!!messageText.length} onSubmit={handleSendMessage}>
              <Input
                ref={inputRef}
                data-size="medium"
                placeholder={
                  selectedChat || selectedChatIds.length !== 0
                    ? i18n.t('placeholder.typeMessage')
                    : i18n.t('placeholder.chooseChatFirst')
                }
                value={messageText}
                onChange={(e: any) => setMessageText(e.target.value)}
                multiline
                maxRows={4}
                onKeyDown={handleKeyDown}
                disabled={(selectedChatIds.length === 0 && !selectedChat) || isLoadingMessages}
              />
              {showToRoles(['SUPER_ADMIN'], user) && showChatsList && (
                <Button
                  id="chat-select-all"
                  data-color="white"
                  type="button"
                  variant="outlined"
                  size="medium"
                  isSquare
                  onClick={() =>
                    setSelectedChatIds((prev) =>
                      prev.length === data.length ? [] : data.map((chat) => chat.id),
                    )
                  }
                >
                  <ChecklistIcon />
                </Button>
              )}
              <Button
                data-color="primary"
                size="medium"
                type="submit"
                isSquare
                disabled={
                  (selectedChatIds.length === 0 && !selectedChat) ||
                  !messageText.trim() ||
                  isLoadingMessages
                }
              >
                <SendIcon />
              </Button>
            </ChatFooter>
          )}
        </ChatPanel>
      )}

      {(!isSmallScreen || !isOpen) && (
        <Badge
          badgeContent={''}
          data-color="blue"
          data-size="small"
          invisible={!data.some((chat) => chat._count.messages > 0)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          overlap="circular"
        >
          <Button
            data-color="appbar-dropdown-header"
            variant="outlined"
            isSquare
            onClick={handleToggleOpen}
          >
            <ChatBubbleOutlineIcon />
          </Button>
        </Badge>
      )}
    </ChatWrapper>
  );
};
